import { initializeApp } from 'firebase/app';
import {
  getAuth, connectAuthEmulator,
  GoogleAuthProvider, EmailAuthProvider,
} from 'firebase/auth';
import { getFirestore, connectFirestoreEmulator } from 'firebase/firestore';
import { getStorage, connectStorageEmulator } from 'firebase/storage';
import { getFunctions, connectFunctionsEmulator } from 'firebase/functions';
import { initializeAppCheck, ReCaptchaV3Provider, getToken as getAppCheckTokenRaw } from 'firebase/app-check';
import { firebaseConfig, USE_EMULATORS, FUNCTIONS_REGION, RECAPTCHA_V3_SITE_KEY } from './firebaseConfig.js';

export const app = initializeApp(firebaseConfig);

// App Check must be initialized before any other Firebase service is used
// so its token is attached from the very first request. Skipped entirely
// (not just left unenforced) until a real reCAPTCHA v3 site key is
// configured in firebaseConfig.js — see that file's "App Check" comment
// for setup steps. Also skipped under the emulator, where App Check adds
// nothing and just needs its own debug-token dance.
let appCheckInstance = null;
if (RECAPTCHA_V3_SITE_KEY && !USE_EMULATORS) {
  appCheckInstance = initializeAppCheck(app, {
    provider: new ReCaptchaV3Provider(RECAPTCHA_V3_SITE_KEY),
    isTokenAutoRefreshEnabled: true,
  });
}

// Used by the hand-rolled onRequest gateways (generate/suggestTopics —
// see src/lib/generate.js, suggestTopics.js). Firestore/Storage/onCall
// functions get their App Check token attached automatically by the SDK;
// these don't, since they're plain fetch() calls, not SDK calls, so each
// one must ask for the token and send it as a header itself. Returns null
// (never throws) whenever App Check isn't configured yet, so these
// gateways work exactly as before until RECAPTCHA_V3_SITE_KEY is filled in.
//
// Timed out, not just try/catch'd: if the reCAPTCHA v3 script itself is
// blocked (ad blocker, privacy extension, a network filter on Google's
// recaptcha domains), the underlying SDK call can sit pending forever —
// it never resolves and never rejects, so a bare try/catch does nothing
// and every caller (src/lib/generate.js, suggestTopics.js) hangs before
// its own fetch ever goes out, with no console error and no network
// request to show for it. A few seconds without a token is exactly the
// same as App Check being unconfigured — fail open, don't block the user.
async function withTimeout(promise, ms) {
  let timer;
  const timeout = new Promise((_, reject) => {
    timer = setTimeout(() => reject(new Error(`timed out after ${ms}ms`)), ms);
  });
  try {
    return await Promise.race([promise, timeout]);
  } finally {
    clearTimeout(timer);
  }
}

export async function getAppCheckHeader() {
  if (!appCheckInstance) return {};
  try {
    const { token } = await withTimeout(getAppCheckTokenRaw(appCheckInstance, false), 5000);
    return token ? { 'X-Firebase-AppCheck': token } : {};
  } catch (err) {
    console.warn('[Learning Lab] App Check token fetch failed:', err.message);
    return {};
  }
}

export const auth = getAuth(app);
export const db = getFirestore(app);
export const storage = getStorage(app);
export const functions = getFunctions(app, FUNCTIONS_REGION);

export const googleProvider = new GoogleAuthProvider();

if (USE_EMULATORS) {
  connectAuthEmulator(auth, 'http://127.0.0.1:9099', { disableWarnings: true });
  connectFirestoreEmulator(db, '127.0.0.1', 8080);
  connectStorageEmulator(storage, '127.0.0.1', 9199);
  connectFunctionsEmulator(functions, '127.0.0.1', 5001);
  console.info('[Learning Lab] Using Firebase Emulator Suite');
}
